'use client'

/**
 * OfflineBanner — Global offline connectivity indicator
 *
 * Monitors browser online/offline status and shows a banner
 * when the user loses internet connectivity.
 *
 * INVARIANTS:
 * ✅ No new service — uses browser's native navigator.onLine API
 * ✅ No new authority — pure UI component
 * ✅ No financial data exposed in offline state
 * ✅ Consistent BPAL styling
 * ✅ Auto-hides when connectivity returns
 * ✅ Safe to mount at app root
 *
 * AUTHORITY: app/layout.tsx → Providers → OfflineBanner
 */

import { useEffect, useState } from 'react'

export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(false)
  const [show, setShow] = useState(false)

  useEffect(() => {
    // Initialize from browser state
    setIsOffline(typeof navigator !== 'undefined' ? !navigator.onLine : false)

    const handleOnline = () => {
      setIsOffline(false)
      // Brief delay before hiding so user can see the transition
      setTimeout(() => setShow(false), 500)
    }

    const handleOffline = () => {
      setIsOffline(true)
      setShow(true)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Don't render anything on first mount if online
  if (!show && !isOffline) return null

  // Don't render until we've confirmed offline state
  if (!show && !isOffline) return null

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${
        show || isOffline ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="px-4 py-2 bg-amber-50 border-b border-amber-200/50 text-center">
        <p className="text-xs font-medium text-amber-700">
          No internet connection — some features may be unavailable
        </p>
      </div>
    </div>
  )
}
